import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useBoundStore from 'store';
import { createSaleAd, editSaleAd } from 'api';
import { BuyApartmentFlowPaths, SellApartmentFlowPaths } from 'routes';
import useApartmentSaleInfo, { APIApartmentSellInfoRes } from 'api/hooks/use-apartment-sale-info';
import useFacilityConstants from '../../api/hooks/use-facility-constants';
import BaseScreen from 'components/base-screen';
import BottomSheet from 'components/bottom-sheet';
import Button, { ButtonProps } from 'components/button';
import BottomSheetResult from 'components/bottom-sheet-result';
import ApartmentListItem from 'components/apartment-list-item';
import PriceCalculator from '../../components/price-calculator';
import styles from './styles.module.scss';
import { MarketTypes } from '../../api/types';

export type InputApartmentPriceNavigateProps = {
  apartment: APIApartmentSellInfoRes & {
    id: number;
    price: number;
    hasBurden: boolean;
    hasMaternityCapital: boolean;
    loweredPrice: number;
    minorOwners?: boolean;
  };
  marketTypes: MarketTypes.SECONDARY | MarketTypes.PRIMARY;
};

const InputApartmentPrice: React.FC = () => {
  const navigate = useNavigate();
  const reportConstant = useFacilityConstants();

  const selectedSellComplex = useBoundStore((s) => s.selectedSellComplex);
  const selectedSellBuilding = useBoundStore((s) => s.selectedSellBuilding);
  const sellApartmentNumber = useBoundStore((s) => s.sellApartmentNumber);
  const resetSellFlow = useBoundStore((s) => s.resetSellFlow);
  const forceAuthorize = useBoundStore((s) => s.forceAuthorize);

  const navigationProps = useLocation().state as InputApartmentPriceNavigateProps | undefined;

  const saleInfo = useApartmentSaleInfo(
    selectedSellBuilding.id,
    navigationProps ? '' : sellApartmentNumber,
  );

  const apartmentSellInfo = navigationProps?.apartment ?? saleInfo.apartmentSellInfo;

  interface IPriceStat {
    days: number;
    price: number;
    percent: number;
  }

  const [selectedPrice, setSelectedPrice] = useState<{
    stat: IPriceStat;
    value: number;
  } | null>(null);

  const [priceWithoutMarketPrise, setPriceWithoutMarketPrise] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);

  const buttons: ButtonProps[] = [
    navigationProps
      ? {
          label: 'Сохранить',
          disabled: !priceWithoutMarketPrise,
          loading: isSubmitting,
          onClick: async () => {
            setIsSubmitting(true);
            const editAdRes = await editSaleAd(
              navigationProps.apartment.id,
              priceWithoutMarketPrise!,
            );
            setIsSubmitting(false);

            if (editAdRes.result) {
              navigate(
                navigationProps.marketTypes === MarketTypes.SECONDARY
                  ? SellApartmentFlowPaths.apartmentSaleAdsSecondary
                  : SellApartmentFlowPaths.apartmentSaleAds,
              );
            }
          },
        }
      : {
          label: 'Разместить объявление',
          loading: isSubmitting,
          disabled: !selectedPrice?.stat?.price && !priceWithoutMarketPrise,
          onClick: async () => {
            if (!forceAuthorize()) return;
            setIsSubmitting(true);
            const submitRes = await createSaleAd({
              buildingId: selectedSellBuilding.id,
              apartmentNumber: sellApartmentNumber,
              price: saleInfo?.apartmentSellInfo
                ? Math.floor(selectedPrice?.stat?.price ?? 0)
                : Math.floor(priceWithoutMarketPrise),

              expiresInDays: saleInfo?.apartmentSellInfo ? selectedPrice?.stat?.days : 40,
              typeId: saleInfo?.apartmentSellInfo?.typeId,
            });
            setIsSubmitting(false);

            if (submitRes.result) {
              resetSellFlow();
              setIsBottomSheetOpen(true);
            }
          },
        },
  ];

  const handleDismissBottomSheet = () => {
    setIsBottomSheetOpen(false);
    navigate(BuyApartmentFlowPaths.main);
  };

  return (
    <BaseScreen
      padContent
      title='Установите цену'
      buttons={buttons}
      onBackButtonClick={() => navigate(-1)}
      floatingButtons={false}
    >
      <div className={styles.container}>
        <ApartmentListItem
          isFavorite={false}
          price={priceWithoutMarketPrise}
          squarePrice={
            apartmentSellInfo?.areaInSqM && priceWithoutMarketPrise
              ? ~~(priceWithoutMarketPrise / apartmentSellInfo.areaInSqM)
              : undefined
          }
          image={apartmentSellInfo?.planImage ?? ''}
          rooms={apartmentSellInfo?.roomCount ?? 0}
          area={apartmentSellInfo?.areaInSqM ?? 0}
          housingEstate={selectedSellComplex.title}
          floor={
            apartmentSellInfo
              ? { floor: apartmentSellInfo.floor, total: apartmentSellInfo.floorCount }
              : { floor: 0, total: 0 }
          }
        />
        <PriceCalculator
          report={saleInfo?.apartmentSellInfo}
          reportConstants={reportConstant}
          selectedPrice={selectedPrice}
          setPriceForBackend={setSelectedPrice}
          priceWithoutMarketPrise={priceWithoutMarketPrise}
          setPriceWithoutMarketPrise={setPriceWithoutMarketPrise}
        />
      </div>

      <BottomSheet open={isBottomSheetOpen} onDismiss={handleDismissBottomSheet}>
        <BottomSheetResult type='info' header='Заявка принята'>
          Ваша заявка отправлена на модерацию
        </BottomSheetResult>
        <Button label='Отлично' onClick={handleDismissBottomSheet} />
      </BottomSheet>
    </BaseScreen>
  );
};

export default InputApartmentPrice;
